
import React, { useState } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { Download, Sparkles, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import MessageContent from './messages/MessageContent';
import { useLanguage } from '../contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  aiResponseReceivedAt?: Date;
  isRetryable?: boolean;
  skipIntroAndClosing?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  sessionId?: string;
  onSendMessage?: (message: string) => void;
  onRetryLastRequest?: () => Promise<void>;
  isLastAssistantMessage?: boolean;
  onCopyConversation?: () => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message,
  sessionId,
  onSendMessage, 
  onRetryLastRequest,
  isLastAssistantMessage,
  onCopyConversation
}) => {
  const { t, language } = useLanguage();
  const [isCopied, setIsCopied] = useState(false);
  const isMobile = useIsMobile();

  const handleCopyClick = () => {
    if (onCopyConversation) {
      onCopyConversation();
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  // Note: Agent message tracking is now handled in useChat.ts when the message is received
  // to prevent duplicate tracking on every component re-render

  return (
    <TooltipProvider>
      <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} animate-fade-in d2cbot-bg-white d2cbot-bg-gradient-none`}>
        <div className={`flex ${message.isUser ? 'flex-row-reverse' : 'flex-row'} items-start space-x-2 min-w-0 ${message.isUser ? 'max-w-[95%]' : 'max-w-[95%]'} d2cbot-bg-white d2cbot-bg-gradient-none`}>
          
          <div className="flex flex-col w-full min-w-0">

            <div className={`flex items-center ${message.isUser ? 'justify-end' : 'justify-start'} mb-1 w-full`}>
              <span className="text-xs font-semibold text-gray-600 mr-2">
                {message.isUser ? t('chat.you') : t('chat.agent.name')}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-gray-500 cursor-help">
                    {message.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', ...(isMobile && { second: '2-digit' }) })}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="px-2 py-1 text-xs">
                  <p className="m-0">{message.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                </TooltipContent>
              </Tooltip>
              {!message.isUser && <Sparkles className="w-3 h-3 text-yellow-500 ml-1 animate-pulse" />}
            </div>
            
            <div className="relative">
              <MessageContent 
                content={message.content} 
                isUser={message.isUser}
                sessionId={sessionId}
                onSendMessage={onSendMessage}
                messageTimestamp={message.timestamp}
                isRetryable={message.isRetryable}
                onRetry={onRetryLastRequest}
                skipIntroAndClosing={message.skipIntroAndClosing}
              />
              
              {/* Conversation capture button - only show on last assistant message */}
              {isLastAssistantMessage && !message.isUser && onCopyConversation && (
                <div className="flex justify-end mt-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyClick}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground bg-muted/50 hover:bg-muted border border-border/50 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 max-w-fit"
                        aria-label={t('chat.download.conversation')}
                      >
                        <span className="whitespace-nowrap">{t('chat.download.conversation')}</span>
                        {isCopied ? (
                          <Check className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="px-2 py-1 text-xs max-w-[200px]">
                      <p className="m-0">{t('chat.download.conversation')}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default MessageBubble;
